import * as React from "react";
import { Dialog as DialogPrimitive } from "@base-ui/react/dialog";
import { m } from "framer-motion";
import {
  ArrowLeft01Icon,
  Cancel01Icon,
} from "~/components/icons/central-icons";
import { CentralIcon } from "~/components/icons/central-icons";

import { cn } from "~/lib/utils";
import { Button } from "~/components/ui/button";
import {
  fluidFadeMotion,
  fluidSprings,
  useFluidExitUnmount,
  useFluidOverlayRoot,
} from "~/components/ui/fluid-motion";
import {
  SurfaceProvider,
  useElevatedSurfaceClasses,
} from "~/lib/surface-context";
import { m as messages } from "~/lib/i18n";

type DialogMotionTier = keyof typeof fluidSprings;

type DialogMotionContextValue = {
  actionsRef: React.RefObject<DialogPrimitive.Root.Actions | null>;
  open: boolean;
};

const DialogMotionContext =
  React.createContext<DialogMotionContextValue | null>(null);

function useDialogMotionContext(): DialogMotionContextValue {
  const context = React.useContext(DialogMotionContext);

  if (context) {
    return context;
  }

  return {
    actionsRef: { current: null },
    open: false,
  };
}

function Dialog({
  actionsRef,
  children,
  defaultOpen = false,
  onOpenChange,
  open: controlledOpen,
  ...props
}: DialogPrimitive.Root.Props) {
  const overlayRoot = useFluidOverlayRoot<
    DialogPrimitive.Root.Actions,
    DialogPrimitive.Root.ChangeEventDetails
  >({
    actionsRef,
    defaultOpen,
    onOpenChange,
    open: controlledOpen,
  });

  const contextValue = React.useMemo<DialogMotionContextValue>(
    () => ({
      actionsRef: overlayRoot.actionsRef,
      open: overlayRoot.open,
    }),
    [overlayRoot.actionsRef, overlayRoot.open],
  );

  return (
    <DialogMotionContext.Provider value={contextValue}>
      <DialogPrimitive.Root
        {...overlayRoot.rootProps}
        data-slot="dialog"
        {...props}
      >
        {children}
      </DialogPrimitive.Root>
    </DialogMotionContext.Provider>
  );
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  motionTier = "slow",
  ...props
}: DialogPrimitive.Backdrop.Props & {
  motionTier?: DialogMotionTier;
}) {
  const { open } = useDialogMotionContext();
  const tier = fluidSprings[motionTier];

  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      render={<m.div {...fluidFadeMotion(open, tier)} />}
      className={cn(
        "glider-modal-overlay fixed inset-0 isolate z-50",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  motionTier = "slow",
  showCloseButton = true,
  size = "sm",
  ...props
}: Omit<DialogPrimitive.Popup.Props, "render"> & {
  motionTier?: DialogMotionTier;
  showCloseButton?: boolean;
  size?: "sm" | "md" | "lg" | "wide";
}) {
  const { actionsRef, open } = useDialogMotionContext();
  const surface = useElevatedSurfaceClasses(4, 4);
  const tier = fluidSprings[motionTier];
  const usesScale = motionTier === "fast";

  useFluidExitUnmount(open, tier, () => {
    actionsRef.current?.unmount();
  });

  return (
    <DialogPortal>
      <DialogOverlay motionTier={motionTier} />
      <SurfaceProvider value={surface.level}>
        <DialogPrimitive.Popup
          data-slot="dialog-content"
          data-size={size}
          render={
            <m.div
              initial={{
                opacity: 0,
                scale: usesScale ? 0.97 : 1,
                x: "-50%",
                y: usesScale ? "-50%" : "-48%",
              }}
              animate={{
                opacity: open ? 1 : 0,
                scale: open || !usesScale ? 1 : 0.97,
                x: "-50%",
                y: open || usesScale ? "-50%" : "-48%",
              }}
              transition={open ? tier : tier.exit}
              onAnimationComplete={() => {
                if (!open) {
                  actionsRef.current?.unmount();
                }
              }}
            />
          }
          className={cn(
            surface.className,
            // minmax(0,1fr) so nowrap/truncated children can't inflate the
            // auto track past the popup's max-width (their min-content would
            // otherwise propagate through min-width:auto and get clipped by
            // overflow-hidden).
            "fixed top-1/2 left-1/2 z-50 grid min-w-0 w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)] overflow-hidden gap-6 rounded-4xl p-6 text-meta text-popover-foreground outline-none",
            // md is the shared flow-modal width (trade review, deposit,
            // withdraw) so staged surfaces keep one silhouette. wide is for
            // data-dense surfaces such as expanded charts. Size caps start at
            // sm so narrow viewports stay fluid instead of inheriting a
            // desktop silhouette.
            "sm:data-[size=sm]:max-w-[25rem] sm:data-[size=md]:max-w-[27.5rem] sm:data-[size=lg]:max-w-[33.75rem] sm:data-[size=wide]:max-w-[72rem]",
            className,
          )}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              render={<DialogCloseButton />}
            >
              <CentralIcon className="size-4" icon={Cancel01Icon} />
              <span className="sr-only">
                {messages.components_dialog_close_label()}
              </span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Popup>
      </SurfaceProvider>
    </DialogPortal>
  );
}

/**
 * The standard floating dialog X: one treatment and size (the settings-modal
 * action-surface pattern) on every modal, so staged surfaces — e.g. the trade
 * ticket's review → deposit swap — never show two different close controls.
 * Pass onClick for host-managed closes; the built-in DialogContent close
 * wires it to dismiss by default.
 */
function DialogCloseButton({
  className,
  children,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      aria-label={messages.components_dialog_close_label()}
      className={cn(
        "absolute top-5 right-5 z-10 rounded-full glider-action-surface text-muted-foreground hover:text-foreground",
        className,
      )}
      size="icon-sm"
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <CentralIcon className="size-4" icon={Cancel01Icon} />}
    </Button>
  );
}

/** The back-arrow twin of DialogCloseButton, mirrored to the left corner. */
function DialogBackButton({
  className,
  children,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      aria-label={messages.components_dialog_back_label()}
      className={cn(
        "absolute top-5 left-5 z-10 rounded-full glider-action-surface text-muted-foreground hover:text-foreground",
        className,
      )}
      size="icon-sm"
      type="button"
      variant="ghost"
      {...props}
    >
      {children ?? <CentralIcon className="size-4" icon={ArrowLeft01Icon} />}
    </Button>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("mb-4 flex flex-col gap-1.5", className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          {messages.components_dialog_close_label()}
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "font-heading text-title-sm font-medium text-foreground",
        className,
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        "text-meta text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBackButton,
  DialogClose,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
